// Phase 2.1 Lite — Generate Demo URL modal.
// Used from TemplateLibraryPage to enable / edit a public /demo/{slug} URL
// for an APPROVED template. Backend rejects with a 400 (and the canonical
// message) if the template isn't approved.

import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, ExternalLink, Copy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_DEMO_CONTENT = {
  couple_names: 'Sofia & Moretti',
  date: 'June 14, 2026',
  venue: 'Palácio de Seteais',
  location: 'Sintra, Portugal',
};

export default function GenerateDemoUrlModal({ open, template, token, onClose, onGenerated }) {
  const initialSlug = (template?.demo_slug || template?.slug || '').toString();
  const initialContent = template?.demo_content || DEFAULT_DEMO_CONTENT;

  const [demoSlug, setDemoSlug] = useState(initialSlug);
  const [coupleNames, setCoupleNames] = useState(initialContent.couple_names || DEFAULT_DEMO_CONTENT.couple_names);
  const [date, setDate] = useState(initialContent.date || DEFAULT_DEMO_CONTENT.date);
  const [venue, setVenue] = useState(initialContent.venue || DEFAULT_DEMO_CONTENT.venue);
  const [location, setLocation] = useState(initialContent.location || DEFAULT_DEMO_CONTENT.location);
  const [saving, setSaving] = useState(false);

  // Sync local state when a different template is passed in
  React.useEffect(() => {
    if (!open || !template) return;
    setDemoSlug((template.demo_slug || template.slug || '').toString());
    const c = template.demo_content || DEFAULT_DEMO_CONTENT;
    setCoupleNames(c.couple_names || DEFAULT_DEMO_CONTENT.couple_names);
    setDate(c.date || DEFAULT_DEMO_CONTENT.date);
    setVenue(c.venue || DEFAULT_DEMO_CONTENT.venue);
    setLocation(c.location || DEFAULT_DEMO_CONTENT.location);
  }, [open, template]);

  if (!open || !template) return null;

  const handleGenerate = async () => {
    if (!demoSlug.trim()) {
      toast.error('Demo URL slug is required');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/api/templates/${template.id}/demo/enable`,
        {
          demo_slug: demoSlug.trim(),
          demo_content: {
            couple_names: coupleNames.trim(),
            date: date.trim(),
            venue: venue.trim(),
            location: location.trim(),
          },
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = r.data;
      const fullUrl = `${window.location.origin}/demo/${data.demo_slug}`;
      toast.success('Demo URL generated', {
        description: fullUrl,
        action: {
          label: 'Open',
          onClick: () => window.open(fullUrl, '_blank'),
        },
      });
      onGenerated?.(data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not generate demo URL');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyPreview = async () => {
    if (!demoSlug.trim()) return;
    const url = `${window.location.origin}/demo/${demoSlug.trim()}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="generate-demo-modal"
    >
      <div
        className="w-full max-w-md max-h-[92vh] overflow-y-auto rounded-2xl bg-white dark:bg-gray-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-start justify-between border-b border-gray-100 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900 z-10">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
              <ExternalLink className="w-3.5 h-3.5 text-emerald-500" />
              Generate demo URL
            </h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Create a public link for <strong>{template.name}</strong>. Uses demo content, not real event data.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600"
            data-testid="generate-demo-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Demo URL
            </label>
            <div className="flex items-stretch rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <span className="px-2.5 py-2 text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 font-mono">
                /demo/
              </span>
              <input
                type="text"
                value={demoSlug}
                onChange={(e) => setDemoSlug(e.target.value)}
                placeholder="portuguese-palace"
                className="flex-1 px-3 py-2 text-xs text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 outline-none"
                data-testid="generate-demo-slug-input"
              />
              <button
                onClick={handleCopyPreview}
                type="button"
                title="Copy preview URL"
                disabled={!demoSlug.trim()}
                className="px-2.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border-l border-gray-200 dark:border-gray-700 disabled:opacity-40"
                data-testid="generate-demo-copy-preview-btn"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="mt-1 text-[10px] text-gray-400">
              {window.location.origin}/demo/{demoSlug || '…'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Couple names
              </label>
              <input
                type="text"
                value={coupleNames}
                onChange={(e) => setCoupleNames(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                data-testid="generate-demo-couple-input"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Date
              </label>
              <input
                type="text"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                data-testid="generate-demo-date-input"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Venue
              </label>
              <input
                type="text"
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                data-testid="generate-demo-venue-input"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Location
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                data-testid="generate-demo-location-input"
              />
            </div>
          </div>

          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 px-3 py-2">
            <p className="text-[11px] text-emerald-800 dark:text-emerald-200 leading-relaxed">
              Anyone with the link will be able to view this demo. It uses the template design with demo content — no real event, guest, or RSVP data is exposed.
            </p>
          </div>
        </div>

        <div className="px-5 py-3.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2 bg-gray-50/50 dark:bg-gray-900/50 sticky bottom-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-3.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            data-testid="generate-demo-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={saving || !demoSlug.trim()}
            className="px-4 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="generate-demo-submit-btn"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
            {template.demo_url_enabled ? 'Update demo URL' : 'Generate demo URL'}
          </button>
        </div>
      </div>
    </div>
  );
}
